import { Component } from 'react';
import { CssGeneratorLayout, cssGenCommon as genStyles } from '../converter-layout/css-generator-layout';

interface State {
    tl: number; tr: number; br: number; bl: number;
    elliptical: boolean;
    tlV: number; trV: number; brV: number; blV: number;
    color: string;
    copied: boolean;
}

export default class BorderRadiusGeneratorComponent extends Component<{ feature?: string }, State> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    state: State = {
        tl: 16, tr: 16, br: 16, bl: 16,
        elliptical: false,
        tlV: 16, trV: 16, brV: 16, blV: 16,
        color: '#6366f1',
        copied: false,
    };

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
    }

    private get cssValue(): string {
        const { tl, tr, br, bl, elliptical, tlV, trV, brV, blV } = this.state;
        if (!elliptical) {
            if (tl === tr && tr === br && br === bl) return `${tl}px`;
            return `${tl}px ${tr}px ${br}px ${bl}px`;
        }
        return `${tl}px ${tr}px ${br}px ${bl}px / ${tlV}px ${trV}px ${brV}px ${blV}px`;
    }

    private get cssCode(): string {
        return `border-radius: ${this.cssValue};`;
    }

    copyCss = () => {
        navigator.clipboard.writeText(this.cssCode).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copied: true });
            this.copyTimeout = setTimeout(() => this.setState({ copied: false }), 2000);
        });
    };

    render() {
        const { tl, tr, br, bl, elliptical, tlV, trV, brV, blV, color, copied } = this.state;

        const cornerSlider = (corner: string, value: number, onChange: (v: number) => void) => (
            <div className={genStyles.field}>
                <label>{corner}: <strong>{value}px</strong></label>
                <input type="range" min={0} max={150} value={value} onChange={e => onChange(+e.target.value)} />
            </div>
        );

        const controls = (
            <>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Corners (horizontal radii)</span></div>
                    <div className={genStyles.fieldRow}>
                        {cornerSlider('Top-left', tl, v => this.setState({ tl: v }))}
                        {cornerSlider('Top-right', tr, v => this.setState({ tr: v }))}
                    </div>
                    <div className={genStyles.fieldRow}>
                        {cornerSlider('Bottom-left', bl, v => this.setState({ bl: v }))}
                        {cornerSlider('Bottom-right', br, v => this.setState({ br: v }))}
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', cursor: 'pointer' }}>
                        <input type="checkbox" checked={elliptical}
                            onChange={e => this.setState({ elliptical: e.target.checked })} />
                        Use elliptical (separate horizontal / vertical radii)
                    </label>
                </div>
                {elliptical && (
                    <div className={genStyles.section}>
                        <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Vertical radii</span></div>
                        <div className={genStyles.fieldRow}>
                            {cornerSlider('Top-left', tlV, v => this.setState({ tlV: v }))}
                            {cornerSlider('Top-right', trV, v => this.setState({ trV: v }))}
                        </div>
                        <div className={genStyles.fieldRow}>
                            {cornerSlider('Bottom-left', blV, v => this.setState({ blV: v }))}
                            {cornerSlider('Bottom-right', brV, v => this.setState({ brV: v }))}
                        </div>
                    </div>
                )}
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Color</span></div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input type="color" value={color}
                            style={{ width: 44, height: 38, border: '1px solid #e2e8f0', borderRadius: 6 }}
                            onChange={e => this.setState({ color: e.target.value })} />
                        <input type="text" value={color}
                            style={{ flex: 1, padding: '0.55rem 0.75rem', borderRadius: 6, border: '1px solid #e2e8f0', fontFamily: 'ui-monospace, monospace', fontSize: '0.9rem' }}
                            onChange={e => this.setState({ color: e.target.value })} />
                    </div>
                </div>
            </>
        );

        return (
            <CssGeneratorLayout
                controls={controls}
                previewLabel="Preview"
                preview={<div style={{ width: 180, height: 180, background: color, borderRadius: this.cssValue }} />}
                codeLabel="CSS"
                codeMeta={`${this.cssCode.length} chars`}
                code={this.cssCode}
                codeActions={
                    <button className={`${genStyles.iconBtn} ${copied ? genStyles.iconBtnSuccess : ''}`}
                        onClick={this.copyCss}>
                        {copied ? '✓ Copied' : '📋 Copy CSS'}
                    </button>
                }
            />
        );
    }
}
